/**
 * ============================================================================
 * useApproveMutation — Phase 6c frontend (CCIE).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: POST /api/v1/changes/{id}/approve — approval of an acknowledged
 *          CC. For bulk CCs (item_count > 50) the server requires TWO
 *          distinct signature bundles (CCIE-017 dual approval). For non-bulk
 *          CCs a single signature is accepted.
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';

export interface ApproveInput {
  changeId: string;
  /** Always at least 1, exactly 2 for bulk CCs. */
  signatureBundleIds: string[];
}

export interface ApproveResponse {
  change_id: string;
  status: string;
  approved_at_utc: string;
  post_actions_enqueued: number;
}

export function useApproveMutation(): UseMutationResult<
  ApproveResponse,
  Error,
  ApproveInput
> {
  return useMutation<ApproveResponse, Error, ApproveInput>({
    mutationFn: ({ changeId, signatureBundleIds }) =>
      apiFetch<ApproveResponse>(
        `/api/v1/changes/${encodeURIComponent(changeId)}/approve`,
        {
          method: 'POST',
          body: { signature_bundle_ids: signatureBundleIds },
          idempotencyKey: newIdempotencyKey(),
        },
      ),
  });
}
